import React, { useState } from "react";
import { Check, Copy, Webhook } from "lucide-react";
import Card from "../common/Card";
import SectionLabel from "../common/SectionLabel";

/**
 * SignalSetupCard — the member's personal webhook + TradingView alert template.
 *
 * REAL data: everything here comes from POST /enroll (idempotent per wallet):
 * { webhookUrl, webhookToken, messageTemplate }. This card renders the backend
 * values verbatim — it never fabricates or reformats the template.
 */

const CopyField = ({ label, value, k, copiedKey, onCopy, mono = false }) => (
  <div>
    <div className="flex items-center justify-between mb-1.5">
      <SectionLabel>{label}</SectionLabel>
      <button
        type="button"
        onClick={() => onCopy(value, k)}
        className="inline-flex items-center gap-1.5 text-caption text-arc-ink-2 rounded-arc-sm px-2 py-1 ring-1 ring-arc-line hover:bg-arc-paper-2 transition"
      >
        {copiedKey === k ? <Check size={12} aria-hidden="true" /> : <Copy size={12} aria-hidden="true" />} Copy
      </button>
    </div>
    <div
      className={[
        "text-xs text-arc-ink-2 bg-arc-paper-2 ring-1 ring-arc-line rounded-arc-ctl p-2.5 break-all",
        mono ? "font-mono tnum" : "",
      ].join(" ")}
    >
      {value}
    </div>
  </div>
);

const SignalSetupCard = ({
  enrollment,
  walletConnected = false,
  onEnroll,
  enrollBusy = false,
  enrollError = null,
  enrollSupported = true,
}) => {
  const [copiedKey, setCopiedKey] = useState(null);
  const copy = (text, key) => {
    if (!navigator.clipboard || text == null) return;
    navigator.clipboard.writeText(String(text));
    setCopiedKey(key);
    setTimeout(() => setCopiedKey((k) => (k === key ? null : k)), 1300);
  };

  return (
    <Card hover={false} as="section" aria-label="Signal setup">
      <div className="flex items-center gap-2 mb-1">
        <Webhook size={17} className="text-arc-accent-ink" aria-hidden="true" />
        <h3 className="text-h2 text-arc-ink">Connect your TradingView signal</h3>
      </div>
      <p className="text-body text-arc-ink-2 mb-4 max-w-2xl">
        In TradingView, create an alert on the strategy <em>you</em> choose,
        paste the webhook URL into the alert&apos;s Webhook URL field, and paste
        the message template as the alert message — exactly as shown. That is
        how your signal reaches the engine.
      </p>

      {enrollment?.webhookUrl ? (
        <div className="space-y-3">
          <CopyField
            label="Your webhook URL"
            value={enrollment.webhookUrl}
            k="url"
            copiedKey={copiedKey}
            onCopy={copy}
          />
          <CopyField
            label="Your token"
            value={enrollment.webhookToken}
            k="token"
            copiedKey={copiedKey}
            onCopy={copy}
            mono
          />
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <SectionLabel>TradingView alert message template</SectionLabel>
              <button
                type="button"
                onClick={() => copy(enrollment.messageTemplate, "tmpl")}
                className="inline-flex items-center gap-1.5 text-caption text-arc-ink-2 rounded-arc-sm px-2 py-1 ring-1 ring-arc-line hover:bg-arc-paper-2 transition"
              >
                {copiedKey === "tmpl" ? <Check size={12} aria-hidden="true" /> : <Copy size={12} aria-hidden="true" />} Copy
              </button>
            </div>
            <pre className="text-xs font-mono tnum text-arc-ink-2 bg-arc-paper-2 ring-1 ring-arc-line rounded-arc-ctl p-3 overflow-x-auto whitespace-pre-wrap break-all">
              {enrollment.messageTemplate}
            </pre>
          </div>
          <p className="text-caption text-arc-ink-3">
            Keep your token private — anyone with it can send signals to your
            account. Your signal choice is yours; we don&apos;t recommend or
            provide strategies.
          </p>
        </div>
      ) : (
        /* ARC-52: enrolment needs one free personal_sign, so it cannot run on
           its own — the member has to ask for it. Before the branches were
           consolidated this was a bare sentence and the request went out
           unprompted; the request is now theirs to make. */
        <div className="space-y-2">
          <p className="text-sm text-arc-ink-3">
            {walletConnected
              ? "Sign once with your wallet to generate your personal webhook and alert template. The signature is free, it isn't a transaction, and it gives no one permission to move your funds."
              : "Connect your wallet to generate your personal webhook and alert template."}
          </p>
          {walletConnected && onEnroll ? (
            <button
              type="button"
              onClick={onEnroll}
              disabled={enrollBusy || !enrollSupported}
              className="inline-flex items-center gap-1.5 text-caption text-arc-ink rounded-arc-sm px-3 py-1.5 ring-1 ring-arc-line hover:bg-arc-paper-2 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {enrollBusy ? "Waiting for your signature…" : "Sign to generate my webhook"}
            </button>
          ) : null}
          {enrollError?.message ? (
            <p role="status" className="text-caption text-arc-neg">
              {enrollError.message}
            </p>
          ) : null}
        </div>
      )}
    </Card>
  );
};

export default SignalSetupCard;
